/**
 * Commander partnership rules.
 *
 * A Commander deck normally has a single commander, but several "partner
 * variant" keywords let a deck run two commanders (and therefore 98 other
 * cards instead of 99). This module models every variant so the deck builder
 * can decide whether two cards may share the command zone:
 *
 * - `Partner`                 — both cards need plain Partner
 * - `Partner with [name]`     — the two cards must name each other
 * - `Partner—[quality]`       — both cards need the same restricted label
 * - `Friends forever`         — both cards need Friends forever
 * - `Doctor's companion`      — the other commander must be a Time Lord Doctor
 * - `Choose a Background`     — the other commander must be a Background enchantment
 *
 * Detection is oracle-text based because Scryfall's `keywords` array is
 * inconsistently cased (e.g. `"Choose a background"`) and empty on Backgrounds.
 */

/** Minimal card shape needed for partnership checks. */
export interface CommanderCardInput {
  name?: string | null;
  type_line?: string | null;
  oracle_text?: string | null;
}

export interface PartnerProfile {
  /** Plain `Partner` keyword */
  partner: boolean;
  /** Card named by `Partner with [name]`, null when absent */
  partnerWith: string | null;
  /** Lowercased quality from `Partner—[quality]`, null when absent */
  restrictedPartner: string | null;
  friendsForever: boolean;
  doctorsCompanion: boolean;
  chooseABackground: boolean;
  /** Legendary Background enchantment — only ever a *second* commander */
  isBackground: boolean;
  /** Legendary Creature — Time Lord Doctor (pairs with Doctor's companion) */
  isTimeLordDoctor: boolean;
}

// ============================================================================
// Text parsing
// ============================================================================

/** Card-level oracle text / type lines join faces with " // "; treat each face separately. */
const FACE_SEPARATOR = /\s*\/\/\s*/;

function getFaces(text: string | null | undefined): string[] {
  if (!text) return [];
  return text.split(FACE_SEPARATOR).filter(Boolean);
}

// Anchored to a line start so mid-sentence mentions (reminder text, "partner" in
// flavorful wording) don't produce false positives.
const PARTNER_WITH = /^partner with ([^(\n]+)/im;
const RESTRICTED_PARTNER = /^partner\s*[—–-]\s*([^(\n]+)/im;
const PLAIN_PARTNER = /^partner(?=\s*\(|\s*$)/im;
const FRIENDS_FOREVER = /^friends forever(?=\s*\(|\s*$)/im;
const DOCTORS_COMPANION = /^doctor['’]s companion(?=\s*\(|\s*$)/im;
const CHOOSE_A_BACKGROUND = /^choose a background/im;

/** Subtypes (the part after the em dash) across every face, lowercased. */
function getSubtypeWords(typeLine: string | null | undefined): string[] {
  const words: string[] = [];
  for (const face of getFaces(typeLine)) {
    const parts = face.split(/[—–]/);
    if (parts.length < 2) continue;
    words.push(...parts[1].trim().toLowerCase().split(/\s+/).filter(Boolean));
  }
  return words;
}

/** `Legendary Enchantment — Background` — never a legal sole commander. */
export function isBackgroundCard(card: CommanderCardInput): boolean {
  return getSubtypeWords(card.type_line).includes('background');
}

/** `Legendary Creature — Time Lord Doctor` — the partner for Doctor's companion. */
export function isTimeLordDoctorCard(card: CommanderCardInput): boolean {
  const subtypes = getSubtypeWords(card.type_line);
  return subtypes.includes('time') && subtypes.includes('lord') && subtypes.includes('doctor');
}

export function getPartnerProfile(card: CommanderCardInput): PartnerProfile {
  const faces = getFaces(card.oracle_text);

  let partner = false;
  let partnerWith: string | null = null;
  let restrictedPartner: string | null = null;
  let friendsForever = false;
  let doctorsCompanion = false;
  let chooseABackground = false;

  for (const face of faces) {
    const withMatch = face.match(PARTNER_WITH);
    if (withMatch) partnerWith ??= normalizeName(withMatch[1]);

    const restrictedMatch = face.match(RESTRICTED_PARTNER);
    if (restrictedMatch) restrictedPartner ??= restrictedMatch[1].trim().toLowerCase().replace(/\.$/, '');

    if (PLAIN_PARTNER.test(face)) partner = true;
    if (FRIENDS_FOREVER.test(face)) friendsForever = true;
    if (DOCTORS_COMPANION.test(face)) doctorsCompanion = true;
    if (CHOOSE_A_BACKGROUND.test(face)) chooseABackground = true;
  }

  return {
    partner,
    partnerWith,
    restrictedPartner,
    friendsForever,
    doctorsCompanion,
    chooseABackground,
    isBackground: isBackgroundCard(card),
    isTimeLordDoctor: isTimeLordDoctorCard(card),
  };
}

function normalizeName(value: string): string {
  return value.trim().replace(/\.$/, '').toLowerCase();
}

// ============================================================================
// Pairing
// ============================================================================

/**
 * Whether two cards may legally be designated as a deck's two commanders.
 * Order-independent.
 */
export function canPairAsCommanders(a: CommanderCardInput, b: CommanderCardInput): boolean {
  const profileA = getPartnerProfile(a);
  const profileB = getPartnerProfile(b);

  if (profileA.partner && profileB.partner) return true;
  if (profileA.friendsForever && profileB.friendsForever) return true;

  if (profileA.restrictedPartner !== null && profileA.restrictedPartner === profileB.restrictedPartner) return true;

  if (profileA.partnerWith !== null && profileA.partnerWith === normalizeName(b.name ?? '')) return true;
  if (profileB.partnerWith !== null && profileB.partnerWith === normalizeName(a.name ?? '')) return true;

  if (profileA.chooseABackground && profileB.isBackground) return true;
  if (profileB.chooseABackground && profileA.isBackground) return true;

  if (profileA.doctorsCompanion && profileB.isTimeLordDoctor) return true;
  if (profileB.doctorsCompanion && profileA.isTimeLordDoctor) return true;

  return false;
}

/**
 * The first existing commander that `incoming` may share the command zone with.
 * Used to keep a valid partner in place instead of evicting it when a second
 * commander is assigned.
 */
export function findPairableCommander<T extends { card: CommanderCardInput | null | undefined }>(
  incoming: CommanderCardInput,
  existing: T[],
): T | null {
  for (const candidate of existing) {
    if (candidate.card && canPairAsCommanders(incoming, candidate.card)) return candidate;
  }
  return null;
}
